import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClipboard, FiTarget, FiUsers, FiServer, FiShield, FiLock, FiActivity, FiChevronRight, FiChevronLeft, FiCheck, FiDownload, FiRefreshCw, FiCpu, FiLoader, FiDatabase, FiBox, FiSettings, FiGitBranch, FiInfo, FiClock, FiTrash2, FiEye, FiSave } from 'react-icons/fi';
import { downloadWorkshopPDF, getUnifiedWorkshopAI } from '../api';
import LandingZoneArchitecturePreview from '../components/LandingZoneArchitecturePreview';
import { useTenant } from '../components/Layout';

const CLOUD_CATEGORIES = [
  { name: 'Cloud Strategy', icon: FiTarget },
  { name: 'Organization & People', icon: FiUsers },
  { name: 'Platform & Architecture', icon: FiServer },
  { name: 'Governance', icon: FiShield },
  { name: 'Security', icon: FiLock },
  { name: 'Operations', icon: FiActivity },
];

const AI_CATEGORIES = [
  { name: 'AI Strategy', icon: FiTarget },
  { name: 'Data Platform', icon: FiDatabase },
  { name: 'AI Infrastructure', icon: FiBox },
  { name: 'Security & Responsible AI', icon: FiShield },
  { name: 'MLOps / DevOps', icon: FiGitBranch },
];

const CATEGORIES = [...CLOUD_CATEGORIES, ...AI_CATEGORIES];

const BASE_QUESTIONS = [
  'Does the organization have a defined cloud strategy?',
  'Are cloud initiatives aligned with business goals?',
  'Is executive sponsorship established for cloud adoption?',
  'Is there a cloud migration roadmap or plan?',
  'Are cloud financial management practices (FinOps) in place?',
  'Is there a Cloud Center of Excellence (CCoE) or equivalent?',
  'Do teams have defined cloud ownership and accountability models?',
  'Are DevOps or platform engineering practices adopted?',
  'Is there a cloud skills development and training program?',
  'Are organizational change management processes defined for cloud?',
  null,
  null,
  null,
  'Is there a defined application modernization strategy?',
  null,
  null,
  null,
  'Are resource tagging standards defined and enforced?',
  'Is there a cloud resource lifecycle management process?',
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  'Are incident management processes integrated with cloud operations?',
  null,
  'Is automated scaling and self-healing configured?',
  'Are SLAs/SLOs defined for cloud workloads?',
];

const PROVIDER_QUESTIONS = {
  azure: {
    10: 'Are Azure Landing Zones implemented with Management Groups and subscription vending?',
    11: 'Is infrastructure deployed through Infrastructure as Code (Bicep, Terraform, ARM Templates)?',
    12: 'Are networking patterns standardized (hub-spoke with Azure Virtual WAN or Azure Firewall)?',
    14: 'Are container or serverless platforms standardized (AKS, Azure Functions, Container Apps)?',
    15: 'Are Azure Policies enforced across Management Groups and subscriptions?',
    16: 'Is Azure Cost Management with budget alerting and advisor recommendations implemented?',
    19: 'Are compliance requirements mapped to Azure Policy initiatives and regulatory compliance?',
    20: 'Is identity and access centrally managed through Microsoft Entra ID with Conditional Access?',
    21: 'Are secrets and certificates stored in Azure Key Vault with access policies?',
    22: 'Are CIS Azure benchmarks and Microsoft Cloud Security Benchmark monitored?',
    23: 'Is network segmentation implemented with NSGs, Azure Firewall, and Private Endpoints?',
    24: 'Are Microsoft Defender for Cloud and Microsoft Sentinel deployed for threat detection?',
    25: 'Is monitoring centralized with Azure Monitor, Log Analytics, and Application Insights?',
    27: 'Are Azure Backup and Azure Site Recovery configured and tested for disaster recovery?',
  },
  aws: {
    10: 'Are AWS Landing Zones implemented with Control Tower and Account Factory?',
    11: 'Is infrastructure deployed through Infrastructure as Code (CloudFormation, Terraform, CDK)?',
    12: 'Are networking patterns standardized (Transit Gateway, VPC peering, AWS Network Firewall)?',
    14: 'Are container or serverless platforms standardized (EKS, Lambda, ECS/Fargate)?',
    15: 'Are Service Control Policies (SCPs) and AWS Config Rules enforced across accounts?',
    16: 'Is AWS Cost Explorer with budget alerting and Trusted Advisor recommendations implemented?',
    19: 'Are compliance requirements mapped to AWS Config conformance packs and Security Hub standards?',
    20: 'Is identity and access centrally managed through AWS IAM Identity Center with SSO?',
    21: 'Are secrets and certificates stored in AWS Secrets Manager and ACM?',
    22: 'Are CIS AWS benchmarks and AWS Foundational Security Best Practices monitored?',
    23: 'Is network segmentation implemented with Security Groups, NACLs, and PrivateLink?',
    24: 'Are Amazon GuardDuty and AWS Security Hub deployed for threat detection?',
    25: 'Is monitoring centralized with Amazon CloudWatch, CloudTrail, and X-Ray?',
    27: 'Are AWS Backup and cross-region replication configured and tested for disaster recovery?',
  },
  gcp: {
    10: 'Are GCP Landing Zones implemented with Organization hierarchy and Project Factory?',
    11: 'Is infrastructure deployed through Infrastructure as Code (Terraform, Deployment Manager, Pulumi)?',
    12: 'Are networking patterns standardized (Shared VPC, VPC peering, Cloud Interconnect)?',
    14: 'Are container or serverless platforms standardized (GKE, Cloud Run, Cloud Functions)?',
    15: 'Are Organization Policies and Policy Intelligence enforced across projects?',
    16: 'Is Cloud Billing with budget alerting and cost optimization recommendations implemented?',
    19: 'Are compliance requirements mapped to Security Command Center compliance standards?',
    20: 'Is identity and access centrally managed through Google Cloud Identity with BeyondCorp?',
    21: 'Are secrets stored in Secret Manager and certificates managed with Certificate Manager?',
    22: 'Are CIS GCP benchmarks and Security Health Analytics monitored?',
    23: 'Is network segmentation implemented with VPC Firewall Rules and Private Google Access?',
    24: 'Are Security Command Center and Chronicle deployed for threat detection?',
    25: 'Is monitoring centralized with Cloud Monitoring, Cloud Logging, and Cloud Trace?',
    27: 'Are Cloud Backup and cross-region snapshots configured and tested for disaster recovery?',
  },
};

function getQuestions(provider) {
  const p = (provider || 'azure').toLowerCase();
  const overrides = PROVIDER_QUESTIONS[p] || PROVIDER_QUESTIONS.azure;
  return BASE_QUESTIONS.map((q, i) => q || overrides[i] || PROVIDER_QUESTIONS.azure[i]);
}

const QUESTIONS = getQuestions('azure');

const AI_QUESTIONS = [
  'Does the organization have an enterprise AI strategy aligned with business outcomes?',
  'Are AI use cases prioritized and tracked with measurable KPIs?',
  'Is there executive sponsorship and a dedicated team for AI initiatives?',
  'Is there a defined AI ethics and responsible AI policy?',
  'Are AI investments evaluated with a clear ROI framework?',
  'Do you have governed data pipelines or a centralized data lake suitable for AI workloads?',
  'Is data quality management and lineage tracking implemented?',
  'Are feature stores or feature engineering pipelines available?',
  'Is data cataloging and metadata management in place for AI datasets?',
  'Are data access policies and sharing agreements defined for AI workloads?',
  'Is your infrastructure capable of supporting GPU-based AI/ML workloads?',
  'Are scalable compute environments available for model training (cloud or hybrid)?',
  'Are model serving and inference endpoints standardized and monitored?',
  'Is there capacity planning for AI workloads across environments?',
  'Are cost optimization practices in place for AI compute resources?',
  'Do you have responsible AI governance policies and model monitoring mechanisms?',
  'Are AI model outputs auditable with explainability capabilities?',
  'Is there a process for bias detection and fairness evaluation in AI models?',
  'Are data privacy controls (PII, GDPR, HIPAA) enforced for AI training data?',
  'Is adversarial testing or red-teaming conducted for AI/ML models?',
  'Do you have automated pipelines for model training, deployment, and lifecycle management?',
  'Is model versioning and experiment tracking implemented?',
  'Are CI/CD practices extended to ML model deployment (CI/CD for ML)?',
  'Is model performance monitoring and drift detection automated?',
  'Are rollback and canary deployment strategies defined for AI model releases?',
];

const BASE_GUIDANCE = [
  'Evaluate whether a formal cloud strategy document exists that outlines vision, goals, and adoption approach for cloud services.',
  'Assess if cloud projects are directly mapped to business objectives, revenue targets, or operational efficiency goals.',
  'Determine if C-level leadership actively sponsors and champions cloud transformation with budget authority.',
  'Check if a structured migration plan exists with timelines, workload prioritization, and resource allocation.',
  'Evaluate whether FinOps practices like chargeback, showback, and cloud cost optimization are established.',
  'Assess if a CCoE or similar team exists to drive standards, best practices, and governance across the organization.',
  'Determine if RACI matrices or ownership models are defined for cloud resources, subscriptions, and workloads.',
  'Evaluate whether DevOps practices (CI/CD, IaC, automated testing) are adopted across engineering teams.',
  'Check if formal cloud training programs, certifications, and skill-building initiatives are in place.',
  'Assess if change management frameworks are used to manage the organizational impact of cloud adoption.',
  null,
  null,
  null,
  'Determine if there is a clear strategy for modernizing legacy applications (rehost, refactor, rebuild, replace).',
  null,
  null,
  null,
  'Evaluate whether mandatory tagging standards are enforced for cost allocation, ownership, and environment tracking.',
  'Determine if lifecycle management processes exist for decommissioning, rightsizing, and resource cleanup.',
  null,
  null,
  null,
  null,
  null,
  null,
  null,
  'Check if incident management workflows integrate with cloud monitoring for automated alerting and escalation.',
  null,
  'Evaluate whether auto-scaling policies and self-healing mechanisms are configured for critical workloads.',
  'Check if SLAs and SLOs are formally defined, tracked, and reported for cloud services.',
];

const PROVIDER_GUIDANCE = {
  azure: {
    10: 'Evaluate whether Azure Landing Zones are deployed with Management Groups, subscription vending via Bicep/Terraform, and Microsoft Cloud Adoption Framework baselines.',
    11: 'Check if all infrastructure is provisioned via Bicep, ARM Templates, or Terraform with Azure DevOps or GitHub Actions pipelines and version control.',
    12: 'Assess if network topologies follow Azure hub-spoke patterns with Azure Virtual WAN, Azure Firewall, and ExpressRoute/VPN Gateway.',
    14: 'Evaluate whether AKS, Azure Functions, or Container Apps are standardized with governance policies and cluster configurations.',
    15: 'Check if Azure Policy initiatives are assigned at Management Group level with Deny, Audit, and DeployIfNotExists effects enforced.',
    16: 'Assess if Azure Cost Management budgets, alerts, Azure Advisor recommendations, and cost anomaly detection are configured.',
    19: 'Check if regulatory requirements (SOC2, PCI, HIPAA) are mapped to Azure Policy initiatives and Microsoft Defender regulatory compliance.',
    20: 'Evaluate whether Microsoft Entra ID is configured with Conditional Access, PIM, and RBAC across subscriptions.',
    21: 'Assess if secrets, keys, and certificates are stored in Azure Key Vault with RBAC-based or access policy-based authorization.',
    22: 'Check if Microsoft Cloud Security Benchmark and CIS Azure benchmarks are defined and monitored via Defender for Cloud.',
    23: 'Evaluate whether NSGs, Azure Firewall, Private Endpoints, and service endpoints isolate workloads across VNets.',
    24: 'Assess if Microsoft Defender for Cloud, Microsoft Sentinel, and threat intelligence are deployed for detection and response.',
    25: 'Evaluate whether Azure Monitor, Log Analytics workspaces, and Application Insights cover infrastructure and application telemetry.',
    27: 'Assess if Azure Backup and Azure Site Recovery are configured with tested restore procedures and cross-region DR.',
  },
  aws: {
    10: 'Evaluate whether AWS Control Tower Landing Zones are deployed with Account Factory, SCPs, and AWS Well-Architected baselines.',
    11: 'Check if all infrastructure is provisioned via CloudFormation, CDK, or Terraform with CodePipeline or GitHub Actions and version control.',
    12: 'Assess if network topologies follow AWS Transit Gateway patterns with VPC peering, AWS Network Firewall, and Direct Connect.',
    14: 'Evaluate whether EKS, Lambda, or ECS/Fargate are standardized with governance guardrails and cluster/function configurations.',
    15: 'Check if Service Control Policies (SCPs) and AWS Config Rules are enforced across the AWS Organization and member accounts.',
    16: 'Assess if AWS Cost Explorer budgets, CloudWatch billing alerts, Trusted Advisor, and Cost Anomaly Detection are configured.',
    19: 'Check if regulatory requirements (SOC2, PCI, HIPAA) are mapped to AWS Config conformance packs and Security Hub compliance standards.',
    20: 'Evaluate whether AWS IAM Identity Center is configured with SSO federation, permission sets, and RBAC across accounts.',
    21: 'Assess if secrets are stored in AWS Secrets Manager and certificates managed via AWS Certificate Manager (ACM).',
    22: 'Check if CIS AWS benchmarks and AWS Foundational Security Best Practices are enabled and monitored via Security Hub.',
    23: 'Evaluate whether Security Groups, NACLs, AWS PrivateLink, and VPC endpoints isolate workloads across accounts.',
    24: 'Assess if Amazon GuardDuty, AWS Security Hub, and Amazon Detective are deployed for threat detection and investigation.',
    25: 'Evaluate whether Amazon CloudWatch, CloudTrail, and X-Ray cover infrastructure, API activity, and application tracing.',
    27: 'Assess if AWS Backup with cross-region replication and tested restore procedures are configured for disaster recovery.',
  },
  gcp: {
    10: 'Evaluate whether GCP Landing Zones are deployed with Organization hierarchy, Project Factory, and Cloud Foundation Toolkit baselines.',
    11: 'Check if all infrastructure is provisioned via Terraform, Deployment Manager, or Pulumi with Cloud Build pipelines and version control.',
    12: 'Assess if network topologies follow GCP Shared VPC patterns with Cloud NAT, Cloud Armor, and Cloud Interconnect.',
    14: 'Evaluate whether GKE, Cloud Run, or Cloud Functions are standardized with governance policies and cluster configurations.',
    15: 'Check if Organization Policies and Policy Intelligence are enforced across folders and projects in the GCP Organization.',
    16: 'Assess if Cloud Billing budgets, alerts, cost recommendations, and FinOps Hub are configured across billing accounts.',
    19: 'Check if regulatory requirements (SOC2, PCI, HIPAA) are mapped to Security Command Center compliance standards and Assured Workloads.',
    20: 'Evaluate whether Google Cloud Identity is configured with BeyondCorp, context-aware access, and IAM roles across projects.',
    21: 'Assess if secrets are stored in Secret Manager and certificates managed via Certificate Manager with automatic rotation.',
    22: 'Check if CIS GCP benchmarks and Security Health Analytics are enabled and monitored via Security Command Center.',
    23: 'Evaluate whether VPC Firewall Rules, Private Google Access, and VPC Service Controls isolate workloads across projects.',
    24: 'Assess if Security Command Center, Chronicle SIEM, and Event Threat Detection are deployed for threat detection.',
    25: 'Evaluate whether Cloud Monitoring, Cloud Logging, and Cloud Trace cover infrastructure and application observability.',
    27: 'Assess if Cloud Backup, cross-region snapshots, and tested restore procedures are configured for disaster recovery.',
  },
};

function getGuidance(provider) {
  const p = (provider || 'azure').toLowerCase();
  const overrides = PROVIDER_GUIDANCE[p] || PROVIDER_GUIDANCE.azure;
  return BASE_GUIDANCE.map((g, i) => g || overrides[i] || PROVIDER_GUIDANCE.azure[i]);
}

const QUESTION_GUIDANCE = getGuidance('azure');

const AI_QUESTION_GUIDANCE = [
  'Evaluate whether a formal AI strategy document exists that aligns AI initiatives with business outcomes and competitive advantages.',
  'Assess if AI use cases are cataloged with clear success metrics, business value estimates, and tracking mechanisms.',
  'Determine if dedicated AI leadership (Chief AI Officer, AI team lead) exists with budget authority and organizational mandate.',
  'Check if responsible AI policies cover fairness, transparency, accountability, and are enforced in practice.',
  'Evaluate whether AI investments are assessed with clear ROI frameworks including total cost of ownership and value realization.',
  'Assess if a centralized data lake or lakehouse architecture exists with governed pipelines for AI/ML data ingestion and processing.',
  'Evaluate whether data quality rules, lineage tracking, and data profiling are automated for AI training datasets.',
  'Check if feature stores (e.g., Feast, Tecton, SageMaker Feature Store) are available for reusable feature engineering.',
  'Assess if a data catalog (e.g., Purview, Glue Catalog, Data Catalog) provides discoverability and metadata for AI datasets.',
  'Determine if data sharing agreements, access controls, and cross-team data governance are defined for AI workloads.',
  'Evaluate whether GPU/TPU compute resources (e.g., Azure ML Compute, EC2 P4d, Vertex AI) are available for model training.',
  'Check if scalable training environments support distributed training, spot instances, and auto-scaling compute clusters.',
  'Assess if model serving infrastructure (e.g., Azure ML Endpoints, SageMaker Endpoints, Vertex AI Prediction) is standardized.',
  'Evaluate whether capacity planning accounts for AI workload growth, burst training, and inference scaling requirements.',
  'Check if cost optimization strategies (spot instances, reserved capacity, auto-shutdown) are applied to AI compute.',
  'Assess if AI governance policies cover model risk management, compliance, and responsible AI principles in production.',
  'Evaluate whether model explainability tools (SHAP, LIME, InterpretML) are integrated into the AI pipeline.',
  'Check if automated bias detection and fairness evaluation are part of the model validation process.',
  'Assess if PII detection, data anonymization, and regulatory compliance controls are enforced for AI training data.',
  'Evaluate whether adversarial testing, red-teaming, or robustness testing is conducted for deployed AI models.',
  'Check if end-to-end ML pipelines (training, validation, deployment, monitoring) are automated and reproducible.',
  'Assess if experiment tracking (MLflow, Weights & Biases, Azure ML) captures model versions, parameters, and metrics.',
  'Evaluate whether CI/CD practices include automated model testing, validation gates, and staged deployments.',
  'Check if model performance monitoring detects data drift, concept drift, and performance degradation in production.',
  'Assess if rollback mechanisms and canary/shadow deployments are defined for safe AI model releases.',
];

const OPTION_LABELS = [
  'Not implemented',
  'Initial discussions',
  'Pilot/partial',
  'Implemented but inconsistent',
  'Fully implemented & standardized',
];

const OPTION_COLORS = [
  { bg: '#fef2f2', border: '#dc2626', text: '#991b1b' },
  { bg: '#fff7ed', border: '#ea580c', text: '#9a3412' },
  { bg: '#fefce8', border: '#ca8a04', text: '#854d0e' },
  { bg: '#eff6ff', border: '#2563eb', text: '#1e40af' },
  { bg: '#f0fdf4', border: '#059669', text: '#065f46' },
];

const MATURITY_SCALE = [
  { score: 1, label: 'Not implemented', description: 'No implementation or ad-hoc experimentation' },
  { score: 2, label: 'Initial discussions', description: 'Initial experimentation with limited governance' },
  { score: 3, label: 'Pilot/partial', description: 'Defined processes with partial implementation' },
  { score: 4, label: 'Implemented', description: 'Standardized and automated environment' },
  { score: 5, label: 'Fully standardized', description: 'Fully optimized platform with governance and automation' },
];

const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: '1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

function getMaturityLabel(pct) {
  if (pct <= 20) return { label: 'Not Started', bg: '#fee2e2', color: '#dc2626' };
  if (pct <= 40) return { label: 'Initiating', bg: '#ffedd5', color: '#ea580c' };
  if (pct <= 60) return { label: 'Developing', bg: '#fef3c7', color: '#d97706' };
  if (pct <= 80) return { label: 'Established', bg: '#dbeafe', color: '#2563eb' };
  return { label: 'Optimized', bg: '#dcfce7', color: '#059669' };
}

function getCategoryScore(answers, catIndex, questionsPerCat = 5) {
  let sum = 0, count = 0;
  for (let i = catIndex * questionsPerCat; i < catIndex * questionsPerCat + questionsPerCat; i++) {
    if (answers[i] !== undefined) { sum += answers[i]; count++; }
  }
  if (count === 0) return 0;
  return Math.round((sum / (count * 5)) * 100);
}

function getOverallScore(answers, totalQ) {
  const vals = Object.values(answers);
  if (vals.length === 0) return 0;
  return Math.round((vals.reduce((a, b) => a + b, 0) / (vals.length * 5)) * 100);
}

function MaturityLegend() {
  return (
    <div style={{
      display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '0.5rem 0.75rem',
      background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: '1rem',
    }}>
      <FiInfo style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 2, flexShrink: 0 }} />
      {MATURITY_SCALE.map((m) => (
        <span key={m.score} style={{ fontSize: '0.68rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
          <strong style={{ color: OPTION_COLORS[m.score - 1].text }}>{m.score}</strong> = {m.description}
          {m.score < 5 ? ' | ' : ''}
        </span>
      ))}
    </div>
  );
}

function RadarChart({ categories, scores, size = 360, color = '#4f46e5' }) {
  const center = size / 2;
  const radius = size * 0.36;
  const angleStep = (2 * Math.PI) / categories.length;
  const rings = [0.2, 0.4, 0.6, 0.8, 1.0];

  const getPoint = (index, value) => {
    const angle = (index * angleStep) - Math.PI / 2;
    const r = radius * value;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };

  const polygonPoints = scores.map((s, i) => {
    const p = getPoint(i, s / 100);
    return `${p.x},${p.y}`;
  }).join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {rings.map((ring, ri) => {
        const pts = Array.from({ length: categories.length }, (_, i) => {
          const p = getPoint(i, ring);
          return `${p.x},${p.y}`;
        }).join(' ');
        return <polygon key={ri} points={pts} fill="none" stroke="#e5e7eb" strokeWidth="1" />;
      })}
      {Array.from({ length: categories.length }, (_, i) => {
        const p = getPoint(i, 1);
        return <line key={i} x1={center} y1={center} x2={p.x} y2={p.y} stroke="#e5e7eb" strokeWidth="1" />;
      })}
      <polygon points={polygonPoints} fill={`${color}33`} stroke={color} strokeWidth="2" />
      {scores.map((s, i) => {
        const p = getPoint(i, s / 100);
        return <circle key={i} cx={p.x} cy={p.y} r="4" fill={color} />;
      })}
      {categories.map((cat, i) => {
        const labelDist = categories.length > 8 ? 1.22 : 1.18;
        const labelPoint = getPoint(i, labelDist);
        const labelSize = categories.length > 8 ? '7.5' : '9';
        const label = categories.length > 8 && cat.name.length > 16 ? cat.name.slice(0, 15) + '...' : cat.name;
        return (
          <text key={i} x={labelPoint.x} y={labelPoint.y} textAnchor="middle" dominantBaseline="middle"
            fontSize={labelSize} fill="#6b7280" fontWeight="500">
            {label}
          </text>
        );
      })}
    </svg>
  );
}

export default function DiscoveryWorkshop() {
  const navigate = useNavigate();
  const { selectedTenantId, tenants } = useTenant();
  const [workshopPhase, setWorkshopPhase] = useState('overview');
  const [answers, setAnswers] = useState({});
  const [explanations, setExplanations] = useState({});
  const [aiAnswers, setAiAnswers] = useState({});
  const [aiExplanations, setAiExplanations] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [unifiedAI, setUnifiedAI] = useState(null);
  const [unifiedAILoading, setUnifiedAILoading] = useState(false);
  const [unifiedAIError, setUnifiedAIError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [stageGuidanceData, setStageGuidanceData] = useState({});

  const selectedTenant = tenants.find(t => String(t.id || t.tenant_id) === String(selectedTenantId));
  const cloudProvider = selectedTenant?.cloud_provider || 'azure';
  const providerQuestions = useMemo(() => getQuestions(cloudProvider), [cloudProvider]);
  const providerGuidance = useMemo(() => getGuidance(cloudProvider), [cloudProvider]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('lz_discovery_workshop');
      if (saved) setAnswers(JSON.parse(saved));
      const savedExpl = localStorage.getItem('lz_discovery_explanations');
      if (savedExpl) setExplanations(JSON.parse(savedExpl));
      const savedAi = localStorage.getItem('lz_discovery_ai_answers');
      if (savedAi) setAiAnswers(JSON.parse(savedAi));
      const savedAiExpl = localStorage.getItem('lz_discovery_ai_explanations');
      if (savedAiExpl) setAiExplanations(JSON.parse(savedAiExpl));
      const sessions = localStorage.getItem('lz_discovery_saved_sessions');
      if (sessions) setSavedSessions(JSON.parse(sessions));
    } catch (e) {}
  }, []);

  const saveSession = () => {
    const cloudAnsweredCount_ = Object.keys(answers).length;
    const aiAnsweredCount_ = Object.keys(aiAnswers).length;
    const allVals = [...Object.values(answers), ...Object.values(aiAnswers)];
    const score = allVals.length > 0 ? Math.round((allVals.reduce((a, b) => a + b, 0) / (allVals.length * 5)) * 100) : 0;
    const session = {
      id: Date.now().toString(),
      savedAt: new Date().toISOString(),
      cloudAnswered: cloudAnsweredCount_,
      aiAnswered: aiAnsweredCount_,
      totalQuestions: 30 + AI_QUESTIONS.length,
      overallScore: score,
      answers: { ...answers },
      explanations: { ...explanations },
      aiAnswers: { ...aiAnswers },
      aiExplanations: { ...aiExplanations },
      unifiedAI: unifiedAI || null,
      stageGuidance: Object.keys(stageGuidanceData).length > 0 ? { ...stageGuidanceData } : null,
    };
    const updated = [session, ...savedSessions].slice(0, 10);
    setSavedSessions(updated);
    localStorage.setItem('lz_discovery_saved_sessions', JSON.stringify(updated));
    setSaveConfirm(true);
    setTimeout(() => setSaveConfirm(false), 2500);
  };

  const loadSession = (session) => {
    setAnswers(session.answers || {});
    setExplanations(session.explanations || {});
    setAiAnswers(session.aiAnswers || {});
    setAiExplanations(session.aiExplanations || {});
    setUnifiedAI(session.unifiedAI || null);
    localStorage.setItem('lz_discovery_workshop', JSON.stringify(session.answers || {}));
    localStorage.setItem('lz_discovery_explanations', JSON.stringify(session.explanations || {}));
    localStorage.setItem('lz_discovery_ai_answers', JSON.stringify(session.aiAnswers || {}));
    localStorage.setItem('lz_discovery_ai_explanations', JSON.stringify(session.aiExplanations || {}));
    setWorkshopPhase('summary');
  };

  const deleteSession = (sessionId) => {
    const updated = savedSessions.filter(s => s.id !== sessionId);
    setSavedSessions(updated);
    localStorage.setItem('lz_discovery_saved_sessions', JSON.stringify(updated));
  };

  const downloadSessionPDF = async (session) => {
    setPdfLoading(true);
    try {
      const cloudCats = CLOUD_CATEGORIES.map((cat, i) => {
        const catAnswers = Object.keys(session.answers).filter(k => Math.floor(parseInt(k) / 5) === i);
        const total = catAnswers.reduce((sum, k) => sum + session.answers[k], 0);
        const score = catAnswers.length > 0 ? Math.round((total / (catAnswers.length * 5)) * 100) : 0;
        return { name: cat.name, score, maturity: getMaturityLabel(score).label };
      });
      const aiCats = AI_CATEGORIES.map((cat, i) => {
        const catAnswers = Object.keys(session.aiAnswers || {}).filter(k => Math.floor(parseInt(k) / 5) === i);
        const total = catAnswers.reduce((sum, k) => sum + session.aiAnswers[k], 0);
        const score = catAnswers.length > 0 ? Math.round((total / (catAnswers.length * 5)) * 100) : 0;
        return { name: cat.name, score, maturity: getMaturityLabel(score).label };
      });
      const cloudVals = Object.values(session.answers);
      const oScore = cloudVals.length > 0 ? Math.round((cloudVals.reduce((a, b) => a + b, 0) / (cloudVals.length * 5)) * 100) : 0;
      const aiVals = Object.values(session.aiAnswers || {});
      const aiScore = aiVals.length > 0 ? Math.round((aiVals.reduce((a, b) => a + b, 0) / (aiVals.length * 5)) * 100) : 0;
      const ua = session.unifiedAI;
      const payload = {
        overallScore: oScore,
        categories: cloudCats,
        answers: session.answers,
        questions: providerQuestions.map((q, i) => ({ index: i, question: q, score: session.answers[i] || null })),
        explanations: session.explanations || {},
        aiInsights: ua ? { readinessSummary: ua.readinessSummary, keyObservations: ua.keyObservations, recommendations: ua.recommendations, quickWins: ua.quickWins, biggestRisk: ua.biggestRisk } : null,
        aiReadinessScore: aiScore,
        aiCategories: aiCats,
        aiAnswers: session.aiAnswers || {},
        aiQuestions: AI_QUESTIONS.map((q, i) => ({ index: i, question: q, score: (session.aiAnswers || {})[i] || null })),
        aiExplanations: session.aiExplanations || {},
        aiLandingZoneAnalysis: ua ? { architectureRecommendation: ua.architectureRecommendation, thirtyDayPlan: ua.thirtyDayPlan, maturityAssessment: ua.maturityAssessment, cloudProviderGuidance: ua.cloudProviderGuidance } : null,
        stageGuidance: session.stageGuidance || null,
      };
      const res = await downloadWorkshopPDF(payload);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date(session.savedAt).toISOString().split('T')[0];
      a.download = `Discovery_Workshop_Report_${dateStr}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(answers).length > 0) localStorage.setItem('lz_discovery_workshop', JSON.stringify(answers));
  }, [answers]);
  useEffect(() => {
    if (Object.keys(explanations).length > 0) localStorage.setItem('lz_discovery_explanations', JSON.stringify(explanations));
  }, [explanations]);
  useEffect(() => {
    if (Object.keys(aiAnswers).length > 0) localStorage.setItem('lz_discovery_ai_answers', JSON.stringify(aiAnswers));
  }, [aiAnswers]);
  useEffect(() => {
    if (Object.keys(aiExplanations).length > 0) localStorage.setItem('lz_discovery_ai_explanations', JSON.stringify(aiExplanations));
  }, [aiExplanations]);

  const handleAnswer = (qIndex, score) => setAnswers(prev => ({ ...prev, [qIndex]: score }));
  const handleExplanation = (qIndex, text) => setExplanations(prev => ({ ...prev, [qIndex]: text }));
  const handleAiAnswer = (qIndex, score) => setAiAnswers(prev => ({ ...prev, [qIndex]: score }));
  const handleAiExplanation = (qIndex, text) => setAiExplanations(prev => ({ ...prev, [qIndex]: text }));

  const cloudAnsweredCount = Object.keys(answers).length;
  const aiAnsweredCount = Object.keys(aiAnswers).length;
  const totalCategories = CATEGORIES.length;

  if (workshopPhase === 'overview') {
    return (
      <div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Cloud & AI Landing Zone Discovery Workshop</h1>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0.25rem 0 0' }}>Pre-Assessment Discovery Accelerator for Landing Zone & AI Readiness Governance</p>
        </div>

        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.7, margin: 0 }}>
            This guided discovery workshop captures key information about the customer's cloud environment, operating model, governance posture, and AI readiness before running the Landing Zone Governance Assessment.
          </p>
        </div>

        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <FiClipboard style={{ color: '#4f46e5', fontSize: '1.1rem' }} />
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: 0 }}>Purpose of this Workshop</h3>
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: '#374151', lineHeight: 2 }}>
            <li>Understand the customer cloud strategy</li>
            <li>Identify existing cloud architecture patterns</li>
            <li>Capture governance and operational practices</li>
            <li>Assess AI readiness and data platform maturity</li>
            <li>Generate AI Landing Zone architecture recommendations</li>
            <li>Prepare context for the Landing Zone Governance Assessment</li>
          </ul>
        </div>

        <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>Assessment Categories</h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>11 categories, 55 questions</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {[...CLOUD_CATEGORIES, ...AI_CATEGORIES].map((cat, i) => {
            const Icon = cat.icon;
            return (
              <div key={i} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon style={{ fontSize: '1.2rem', color: '#4f46e5' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>{cat.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>5 questions</div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <button
            onClick={() => setWorkshopPhase('questionnaire')}
            style={{ background: '#4f46e5', color: '#fff', padding: '0.75rem 2rem', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Start Discovery Workshop <FiChevronRight />
          </button>
        </div>

        {savedSessions.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FiClock style={{ color: '#4f46e5', fontSize: '1.1rem' }} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: 0 }}>Saved Results</h3>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{savedSessions.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {savedSessions.map((session) => {
                const date = new Date(session.savedAt);
                const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                const scoreColor = session.overallScore >= 80 ? '#059669' : session.overallScore >= 60 ? '#d97706' : '#dc2626';
                const maturity = getMaturityLabel(session.overallScore);
                return (
                  <div key={session.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: 700, color: scoreColor }}>{session.overallScore}%</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                        Workshop Results — {dateStr}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span>{timeStr}</span>
                        <span>{session.cloudAnswered + session.aiAnswered} of {session.totalQuestions} answered</span>
                        <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 600, background: maturity.bg, color: maturity.color }}>{maturity.label}</span>
                        {session.unifiedAI && <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: '0.68rem', fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>AI Analysis</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                      <button
                        onClick={() => loadSession(session)}
                        title="View Results"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.75rem', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, border: '1px solid #4f46e5', background: '#fff', color: '#4f46e5', cursor: 'pointer' }}
                      >
                        <FiEye /> View
                      </button>
                      <button
                        onClick={() => downloadSessionPDF(session)}
                        disabled={pdfLoading}
                        title="Download PDF"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.75rem', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, border: '1px solid #4f46e5', background: '#fff', color: '#4f46e5', cursor: pdfLoading ? 'not-allowed' : 'pointer', opacity: pdfLoading ? 0.5 : 1 }}
                      >
                        <FiDownload /> PDF
                      </button>
                      <button
                        onClick={() => deleteSession(session.id)}
                        title="Delete"
                        style={{ display: 'flex', alignItems: 'center', padding: '0.4rem', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                      >
                        <FiTrash2 style={{ fontSize: '0.85rem' }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (workshopPhase === 'questionnaire') {
    const isAi = currentCategory >= 6;
    const catLocalIndex = isAi ? currentCategory - 6 : currentCategory;
    const allQs = isAi ? AI_QUESTIONS : providerQuestions;
    const startQ = catLocalIndex * 5;
    const categoryQuestions = allQs.slice(startQ, startQ + 5);
    const guidanceArr = isAi ? AI_QUESTION_GUIDANCE : providerGuidance;
    const currentAnswers = isAi ? aiAnswers : answers;
    const currentExplanations = isAi ? aiExplanations : explanations;
    const handleScore = isAi ? handleAiAnswer : handleAnswer;
    const handleExpl = isAi ? handleAiExplanation : handleExplanation;
    const accentColor = isAi ? '#7c3aed' : '#4f46e5';
    const accentBg = isAi ? '#f5f3ff' : '#eef2ff';

    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: 0 }}>Discovery Workshop</h1>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
            {cloudAnsweredCount + aiAnsweredCount} of {30 + AI_QUESTIONS.length} questions answered
          </p>
        </div>

        <div style={{ ...cardStyle, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Overall Progress</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4f46e5' }}>{Math.round(((cloudAnsweredCount + aiAnsweredCount) / (30 + AI_QUESTIONS.length)) * 100)}%</span>
          </div>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round(((cloudAnsweredCount + aiAnsweredCount) / (30 + AI_QUESTIONS.length)) * 100)}%`, background: '#4f46e5', borderRadius: 3, transition: 'width 0.4s' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {CLOUD_CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            const isActive = currentCategory === i;
            return (
              <button key={i} onClick={() => setCurrentCategory(i)} style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.4rem 0.8rem', borderRadius: 20, fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer',
                border: isActive ? '2px solid #4f46e5' : '1px solid #d1d5db',
                background: isActive ? '#eef2ff' : '#fff', color: isActive ? '#4f46e5' : '#6b7280',
              }}>
                <Icon style={{ fontSize: '0.8rem' }} /> {cat.name}
              </button>
            );
          })}
          {AI_CATEGORIES.map((cat, i) => {
            const catIdx = i + 6;
            const Icon = cat.icon;
            const isActive = currentCategory === catIdx;
            return (
              <button key={catIdx} onClick={() => setCurrentCategory(catIdx)} style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.4rem 0.8rem', borderRadius: 20, fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer',
                border: isActive ? '2px solid #4f46e5' : '1px solid #d1d5db',
                background: isActive ? '#eef2ff' : '#fff', color: isActive ? '#4f46e5' : '#6b7280',
              }}>
                <Icon style={{ fontSize: '0.8rem' }} /> {cat.name}
              </button>
            );
          })}
        </div>

        <MaturityLegend />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
          {categoryQuestions.map((q, idx) => {
            const qIndex = startQ + idx;
            const selected = currentAnswers[qIndex];
            const guidance = guidanceArr[qIndex];
            const expl = currentExplanations[qIndex] || '';
            const globalQNum = isAi ? 30 + qIndex + 1 : qIndex + 1;
            return (
              <div key={qIndex} style={{ ...cardStyle, borderColor: isAi ? '#ddd6fe' : '#e5e7eb' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', marginBottom: '0.4rem' }}>
                  <span style={{ color: accentColor, marginRight: '0.5rem' }}>Q{globalQNum}.</span>{q}
                </div>
                {guidance && (
                  <div style={{
                    fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.6,
                    padding: '0.5rem 0.75rem', background: accentBg, borderRadius: 6,
                    marginBottom: '0.75rem', borderLeft: `3px solid ${accentColor}`,
                  }}>
                    <strong style={{ color: '#475569' }}>Guidance:</strong> {guidance}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {OPTION_LABELS.map((label, optIdx) => {
                    const score = optIdx + 1;
                    const isSelected = selected === score;
                    const oc = OPTION_COLORS[optIdx];
                    return (
                      <div key={score} onClick={() => handleScore(qIndex, score)} style={{
                        flex: '1 1 0', minWidth: 120, padding: '0.6rem 0.5rem', borderRadius: 8,
                        border: isSelected ? `2px solid ${oc.border}` : '1px solid #e5e7eb',
                        background: isSelected ? oc.bg : '#fafafa', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                      }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: isSelected ? oc.text : '#9ca3af' }}>{score}</div>
                        <div style={{ fontSize: '0.68rem', color: isSelected ? oc.text : '#9ca3af', marginTop: 2 }}>{label}</div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '0.3rem' }}>
                    Explanation (optional)
                  </label>
                  <textarea
                    value={expl}
                    onChange={(e) => handleExpl(qIndex, e.target.value)}
                    placeholder="Add additional details or context for this assessment..."
                    rows={2}
                    style={{
                      width: '100%', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #d1d5db',
                      fontSize: '0.8rem', color: '#374151', resize: 'vertical', fontFamily: 'inherit',
                      background: '#fafafa', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={() => setCurrentCategory(prev => prev - 1)}
            disabled={currentCategory === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.6rem 1.5rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600,
              cursor: currentCategory === 0 ? 'not-allowed' : 'pointer',
              border: '1px solid #d1d5db', background: '#fff', color: currentCategory === 0 ? '#d1d5db' : '#374151',
              opacity: currentCategory === 0 ? 0.5 : 1,
            }}
          >
            <FiChevronLeft /> Previous Category
          </button>
          {currentCategory < totalCategories - 1 ? (
            <button
              onClick={() => setCurrentCategory(prev => prev + 1)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.6rem 1.5rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                border: 'none', background: accentColor, color: '#fff',
              }}
            >
              Next Category <FiChevronRight />
            </button>
          ) : (
            <button
              onClick={() => setWorkshopPhase('summary')}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.6rem 1.5rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                border: 'none', background: '#059669', color: '#fff',
              }}
            >
              <FiCheck /> Complete Workshop
            </button>
          )}
        </div>
      </div>
    );
  }

  if (workshopPhase === 'summary') {
    const overallScore = getOverallScore(answers, 30);
    const cloudCategoryScores = CLOUD_CATEGORIES.map((cat, i) => ({
      ...cat,
      score: getCategoryScore(answers, i),
      maturity: getMaturityLabel(getCategoryScore(answers, i)),
    }));

    const aiOverallScore = getOverallScore(aiAnswers, AI_QUESTIONS.length);
    const aiCategoryScores = AI_CATEGORIES.map((cat, i) => ({
      ...cat,
      score: getCategoryScore(aiAnswers, i),
      maturity: getMaturityLabel(getCategoryScore(aiAnswers, i)),
    }));

    const circumference = 2 * Math.PI * 54;

    const fetchUnifiedAI = async () => {
      setUnifiedAILoading(true);
      setUnifiedAIError(null);
      try {
        const res = await getUnifiedWorkshopAI({
          cloudReadinessScore: overallScore,
          cloudCategories: cloudCategoryScores.map(c => ({ name: c.name, score: c.score, maturity: c.maturity.label })),
          cloudAnswers: answers,
          cloudQuestions: providerQuestions.map((q, i) => ({ index: i, question: q, score: answers[i] || null })),
          aiReadinessScore: aiOverallScore,
          aiCategories: aiCategoryScores.map(c => ({ name: c.name, score: c.score, maturity: c.maturity.label })),
          aiAnswers,
          aiQuestions: AI_QUESTIONS.map((q, i) => ({ index: i, question: q, score: aiAnswers[i] || null })),
          explanations,
          aiExplanations,
        });
        setUnifiedAI(res.data);
      } catch (err) {
        setUnifiedAIError(err.response?.data?.detail || 'Failed to generate AI analysis');
      } finally {
        setUnifiedAILoading(false);
      }
    };

    const exportPDF = async () => {
      setPdfLoading(true);
      try {
        const payload = {
          overallScore,
          categories: cloudCategoryScores.map(c => ({ name: c.name, score: c.score, maturity: c.maturity.label })),
          answers,
          questions: providerQuestions.map((q, i) => ({ index: i, question: q, score: answers[i] || null })),
          explanations,
          aiInsights: unifiedAI ? { readinessSummary: unifiedAI.readinessSummary, keyObservations: unifiedAI.keyObservations, recommendations: unifiedAI.recommendations, quickWins: unifiedAI.quickWins, biggestRisk: unifiedAI.biggestRisk } : null,
          aiReadinessScore: aiOverallScore,
          aiCategories: aiCategoryScores.map(c => ({ name: c.name, score: c.score, maturity: c.maturity.label })),
          aiAnswers,
          aiQuestions: AI_QUESTIONS.map((q, i) => ({ index: i, question: q, score: aiAnswers[i] || null })),
          aiExplanations,
          aiLandingZoneAnalysis: unifiedAI ? { architectureRecommendation: unifiedAI.architectureRecommendation, thirtyDayPlan: unifiedAI.thirtyDayPlan, maturityAssessment: unifiedAI.maturityAssessment, cloudProviderGuidance: unifiedAI.cloudProviderGuidance } : null,
          stageGuidance: Object.keys(stageGuidanceData).length > 0 ? stageGuidanceData : null,
        };
        const res = await downloadWorkshopPDF(payload);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Discovery_Workshop_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        alert('Failed to generate PDF. Please try again.');
      } finally {
        setPdfLoading(false);
      }
    };

    const archPillars = unifiedAI?.architectureRecommendation ? Object.values(unifiedAI.architectureRecommendation) : [];
    const thirtyDayPlan = unifiedAI?.thirtyDayPlan;
    const maturityAssmt = unifiedAI?.maturityAssessment;
    const providerGuidance = unifiedAI?.cloudProviderGuidance;

    const pillarIcons = {
      'Identity & Access Architecture': FiUsers,
      'Networking Model': FiServer,
      'Data Platform Architecture': FiDatabase,
      'AI/ML Compute Infrastructure': FiBox,
      'Security & Governance Model': FiShield,
      'MLOps Architecture': FiGitBranch,
    };

    return (
      <div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Discovery Workshop Results</h1>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>Cloud Landing Zone & AI Readiness Summary</p>
        </div>

        {/* Overall Readiness Score */}
        {(() => {
          const totalAnswered = cloudAnsweredCount + aiAnsweredCount;
          const totalQuestions = 30 + AI_QUESTIONS.length;
          const allAnswerValues = [...Object.values(answers), ...Object.values(aiAnswers)];
          const combinedScore = allAnswerValues.length > 0 ? Math.round((allAnswerValues.reduce((a, b) => a + b, 0) / (allAnswerValues.length * 5)) * 100) : 0;
          const combinedOffset = circumference - (combinedScore / 100) * circumference;
          let combinedColor = '#dc2626';
          if (combinedScore >= 80) combinedColor = '#059669';
          else if (combinedScore >= 60) combinedColor = '#d97706';
          return (
            <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <svg width="130" height="130" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={combinedColor} strokeWidth="10"
                  strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={combinedOffset}
                  transform="rotate(-90 70 70)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                <text x="70" y="64" textAnchor="middle" fontSize="26" fontWeight="700" fill="#1f2937">{combinedScore}%</text>
                <text x="70" y="82" textAnchor="middle" fontSize="9" fill="#6b7280">Readiness</text>
              </svg>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', margin: '0 0 0.4rem' }}>Overall Readiness Score</h3>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.5rem' }}>{totalAnswered} of {totalQuestions} questions answered</p>
                <span style={{
                  padding: '3px 12px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700,
                  background: getMaturityLabel(combinedScore).bg, color: getMaturityLabel(combinedScore).color,
                }}>
                  {getMaturityLabel(combinedScore).label}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Category Breakdown */}
        <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Category Breakdown</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          {[...cloudCategoryScores, ...aiCategoryScores].map((cat, i) => {
            const Icon = cat.icon;
            const isAiCat = i >= 6;
            const questionsArr = isAiCat ? AI_QUESTIONS : QUESTIONS;
            const answersObj = isAiCat ? aiAnswers : answers;
            const localIdx = isAiCat ? i - 6 : i;
            return (
              <div key={i} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 8, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon style={{ fontSize: '1rem', color: '#4f46e5' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>{cat.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 2 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>{cat.score}%</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                        background: cat.maturity.bg, color: cat.maturity.color,
                      }}>
                        {cat.maturity.label}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: '0.75rem' }}>
                  <div style={{ height: '100%', width: `${cat.score}%`, background: '#4f46e5', borderRadius: 3, transition: 'width 0.4s' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {questionsArr.slice(localIdx * 5, localIdx * 5 + 5).map((q, qIdx) => {
                    const qIndex = localIdx * 5 + qIdx;
                    const score = answersObj[qIndex];
                    return (
                      <div key={qIndex} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          {[1, 2, 3, 4, 5].map(v => (
                            <div key={v} style={{
                              width: 10, height: 10, borderRadius: '50%',
                              background: score >= v ? (v <= 2 ? '#dc2626' : v <= 3 ? '#d97706' : '#059669') : '#e5e7eb',
                            }} />
                          ))}
                        </div>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Readiness Radar */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Readiness Radar</h3>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RadarChart categories={[...CLOUD_CATEGORIES, ...AI_CATEGORIES]} scores={[...cloudCategoryScores, ...aiCategoryScores].map(c => c.score)} size={420} color="#4f46e5" />
          </div>
        </div>

        {/* Unified AI Insights & Architecture */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem', borderColor: '#c7d2fe' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: 0 }}>AI-Powered Insights & Architecture</h3>
              <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0.2rem 0 0' }}>Unified analysis of cloud readiness, AI maturity, and architecture recommendations</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '0.7rem', color: '#7c3aed', fontWeight: 600, background: '#f5f3ff', padding: '3px 10px', borderRadius: 12, border: '1px solid #ddd6fe' }}>
                <FiCpu style={{ marginRight: 4, verticalAlign: 'middle' }} /> Powered by LLM
              </span>
              {!unifiedAI && !unifiedAILoading && (
                <button onClick={fetchUnifiedAI} style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  padding: '0.4rem 1rem', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
                  border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer',
                }}>
                  <FiCpu /> Generate AI Analysis
                </button>
              )}
            </div>
          </div>

          {unifiedAILoading && (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: '#6b7280' }}>
              <FiLoader style={{ animation: 'spin 1s linear infinite', fontSize: '1.5rem', display: 'block', margin: '0 auto 0.5rem' }} />
              <p style={{ fontSize: '0.85rem', margin: 0 }}>Analyzing your assessment with AI...</p>
              <p style={{ fontSize: '0.75rem', margin: '0.25rem 0 0', color: '#9ca3af' }}>Generating insights, architecture recommendations, and 30-day plan</p>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {unifiedAIError && (
            <div style={{ fontSize: '0.85rem', color: '#991b1b', padding: '0.75rem', background: '#fef2f2', borderRadius: 8 }}>
              {unifiedAIError}
              <button onClick={fetchUnifiedAI} style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Retry
              </button>
            </div>
          )}

          {!unifiedAI && !unifiedAILoading && !unifiedAIError && (
            <div style={{ fontSize: '0.85rem', color: '#6b7280', padding: '1.25rem', background: '#f9fafb', borderRadius: 8, textAlign: 'center' }}>
              <p style={{ margin: '0 0 0.5rem', fontStyle: 'italic' }}>Click "Generate AI Analysis" to receive a comprehensive report including key observations, prioritized recommendations, architecture pillars, and a 30-day implementation plan.</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#9ca3af' }}>Your scores and free-text explanations will be analyzed for context-aware recommendations.</p>
            </div>
          )}

          {unifiedAI && (
            <div>
              {unifiedAI.readinessSummary && (
                <div style={{ fontSize: '0.85rem', color: '#374151', padding: '0.75rem', background: '#f0f4ff', borderRadius: 8, lineHeight: 1.7, border: '1px solid #c7d2fe', marginBottom: '1rem' }}>
                  {unifiedAI.readinessSummary}
                </div>
              )}

              {unifiedAI.keyObservations?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {unifiedAI.keyObservations.map((obs, i) => {
                    const typeStyles = {
                      critical: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'CRITICAL' },
                      warning: { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: 'WARNING' },
                      strength: { bg: '#f0fdf4', color: '#065f46', border: '#bbf7d0', label: 'STRENGTH' },
                      insight: { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', label: 'INSIGHT' },
                    };
                    const s = typeStyles[obs.type] || typeStyles.insight;
                    return (
                      <div key={i} style={{ fontSize: '0.85rem', color: s.color, padding: '0.6rem 0.75rem', background: s.bg, borderRadius: 8, border: `1px solid ${s.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 6, fontWeight: 700, background: s.color, color: '#fff' }}>{s.label}</span>
                          <strong>{obs.title}</strong>
                        </div>
                        <div style={{ fontWeight: 400 }}>{obs.detail}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {unifiedAI.recommendations?.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: '0 0 0.5rem' }}>Prioritized Recommendations</h4>
                  {unifiedAI.recommendations.map((rec, i) => {
                    const impactColors = { High: '#dc2626', Medium: '#d97706', Low: '#059669' };
                    return (
                      <div key={i} style={{ fontSize: '0.85rem', padding: '0.6rem 0.75rem', background: '#f9fafb', borderRadius: 8, marginBottom: '0.5rem', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <strong>#{rec.priority}. {rec.title}</strong>
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 10, fontWeight: 600, color: impactColors[rec.impact] || '#d97706', background: rec.impact === 'High' ? '#fef2f2' : rec.impact === 'Low' ? '#f0fdf4' : '#fffbeb' }}>
                            {rec.impact} Impact
                          </span>
                          <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{rec.category}</span>
                        </div>
                        <div style={{ color: '#374151' }}>{rec.description}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                {unifiedAI.quickWins?.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', margin: '0 0 0.4rem' }}>Quick Wins</h4>
                    {unifiedAI.quickWins.map((qw, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', color: '#065f46', padding: '0.4rem 0.6rem', background: '#f0fdf4', borderRadius: 6, marginBottom: '0.3rem' }}>
                        {qw}
                      </div>
                    ))}
                  </div>
                )}
                {unifiedAI.biggestRisk && (
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626', margin: '0 0 0.4rem' }}>Biggest Risk</h4>
                    <div style={{ fontSize: '0.8rem', color: '#991b1b', padding: '0.6rem', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca', lineHeight: 1.6 }}>
                      {unifiedAI.biggestRisk}
                    </div>
                  </div>
                )}
              </div>

              {maturityAssmt && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: '0 0 0.5rem' }}>AI Maturity Assessment</h4>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem' }}><strong>Current:</strong> {maturityAssmt.currentLevel}</span>
                    <span style={{ fontSize: '0.8rem' }}><strong>Target:</strong> {maturityAssmt.targetLevel}</span>
                  </div>
                  <p style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, margin: '0 0 0.5rem' }}>{maturityAssmt.gapAnalysis}</p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669', marginBottom: '0.3rem' }}>Strengths</div>
                      {maturityAssmt.strengthAreas?.map((s, i) => (
                        <div key={i} style={{ fontSize: '0.78rem', color: '#065f46', padding: '0.25rem 0.5rem', background: '#f0fdf4', borderRadius: 4, marginBottom: '0.2rem' }}>{s}</div>
                      ))}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', marginBottom: '0.3rem' }}>Improvement Areas</div>
                      {maturityAssmt.improvementAreas?.map((s, i) => (
                        <div key={i} style={{ fontSize: '0.78rem', color: '#991b1b', padding: '0.25rem 0.5rem', background: '#fef2f2', borderRadius: 4, marginBottom: '0.2rem' }}>{s}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Architecture Pillars */}
              <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: '0 0 0.75rem' }}>Recommended Architecture</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {archPillars.map((pillar, i) => {
                  const PillarIcon = pillarIcons[pillar.title] || FiSettings;
                  return (
                    <div key={i} style={{ ...cardStyle, padding: '1rem', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PillarIcon style={{ fontSize: '0.9rem', color: '#7c3aed' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827' }}>{pillar.title}</div>
                          <div style={{ fontSize: '0.7rem', color: '#7c3aed', fontWeight: 600 }}>{pillar.maturityLevel}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
                        {pillar.recommendations?.map((rec, ri) => (
                          <div key={ri} style={{ fontSize: '0.78rem', color: '#374151', padding: '0.2rem 0', lineHeight: 1.5 }}>
                            <span style={{ color: '#7c3aed', marginRight: '0.3rem' }}>&#8226;</span>{rec}
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {pillar.azureServices?.slice(0, 3).map((s, si) => (
                          <span key={`az-${si}`} style={{ fontSize: '0.63rem', padding: '2px 6px', borderRadius: 4, background: '#eff6ff', color: '#1e40af', fontWeight: 500 }}>{s}</span>
                        ))}
                        {pillar.awsServices?.slice(0, 2).map((s, si) => (
                          <span key={`aws-${si}`} style={{ fontSize: '0.63rem', padding: '2px 6px', borderRadius: 4, background: '#fff7ed', color: '#9a3412', fontWeight: 500 }}>{s}</span>
                        ))}
                        {pillar.gcpServices?.slice(0, 2).map((s, si) => (
                          <span key={`gcp-${si}`} style={{ fontSize: '0.63rem', padding: '2px 6px', borderRadius: 4, background: '#f0fdf4', color: '#065f46', fontWeight: 500 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 30-Day Plan */}
              {thirtyDayPlan && (
                <>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: '0 0 0.75rem' }}>30-Day AI Foundation Plan</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {Object.entries(thirtyDayPlan).map(([weekKey, week], i) => (
                      <div key={weekKey} style={{ ...cardStyle, padding: '1rem', borderLeft: `4px solid ${['#4f46e5', '#7c3aed', '#059669', '#d97706'][i] || '#4f46e5'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                          <span style={{
                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                            background: ['#eef2ff', '#f5f3ff', '#f0fdf4', '#fffbeb'][i] || '#eef2ff',
                            color: ['#4f46e5', '#7c3aed', '#059669', '#d97706'][i] || '#4f46e5',
                          }}>
                            Week {i + 1}
                          </span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>{week.title}</span>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.4rem', lineHeight: 1.5 }}>{week.description}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          {week.actions?.map((action, ai) => (
                            <div key={ai} style={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.5 }}>
                              <FiCheck style={{ color: '#059669', marginRight: '0.3rem', verticalAlign: 'middle', fontSize: '0.7rem' }} />
                              {action}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {providerGuidance && (() => {
                const providerDefs = [
                  { key: 'azure', label: 'Microsoft Azure', color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' },
                  { key: 'aws', label: 'Amazon Web Services', color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' },
                  { key: 'gcp', label: 'Google Cloud', color: '#065f46', bg: '#f0fdf4', border: '#bbf7d0' },
                ];
                const activeProvider = providerDefs.find(p => p.key === cloudProvider) || providerDefs[0];
                const data = providerGuidance[activeProvider.key];
                if (!data) return null;
                return (
                  <>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: '0 0 0.75rem' }}>{activeProvider.label} Guidance</h4>
                    <div style={{ ...cardStyle, padding: '1.25rem', borderColor: activeProvider.border, marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: activeProvider.color, marginBottom: '0.5rem' }}>{activeProvider.label}</div>
                      <p style={{ fontSize: '0.84rem', color: '#374151', lineHeight: 1.6, margin: '0 0 0.5rem' }}>{data.summary}</p>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.4rem' }}>Architecture Pattern: {data.architecturePattern}</div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {data.keyServices?.map((s, si) => (
                          <span key={si} style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: 5, background: activeProvider.bg, color: activeProvider.color, fontWeight: 500 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div style={{ ...cardStyle, marginBottom: '1.5rem', background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>Recommended Next Step</h3>
          <p style={{ fontSize: '0.85rem', color: '#374151', margin: 0, lineHeight: 1.7 }}>
            Based on the discovery workshop results, proceed to the Landing Zone Governance Assessment to conduct a detailed maturity evaluation across 8 governance pillars with 46 assessment items.
          </p>
        </div>

        <LandingZoneArchitecturePreview answers={answers} aiAnswers={aiAnswers} cloudProvider={cloudProvider} onStageDataChange={setStageGuidanceData} />

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
          <button
            onClick={() => navigate('/gov-framework')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 2rem', borderRadius: 10, fontSize: '0.95rem', fontWeight: 700,
              border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer',
            }}
          >
            Proceed to Landing Zone Assessment <FiChevronRight />
          </button>
          <button
            onClick={saveSession}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 1.5rem', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600,
              border: '2px solid #059669', background: saveConfirm ? '#ecfdf5' : '#fff', color: '#059669', cursor: 'pointer',
              transition: 'background 0.3s',
            }}
          >
            {saveConfirm ? <><FiCheck /> Saved!</> : <><FiSave /> Save Results</>}
          </button>
          <button
            onClick={exportPDF}
            disabled={pdfLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 1.5rem', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600,
              border: '2px solid #4f46e5', background: '#fff', color: '#4f46e5', cursor: pdfLoading ? 'not-allowed' : 'pointer',
              opacity: pdfLoading ? 0.6 : 1,
            }}
          >
            <FiDownload /> {pdfLoading ? 'Generating PDF...' : 'Export PDF'}
          </button>
          <button
            onClick={() => {
              setAnswers({});
              setExplanations({});
              setAiAnswers({});
              setAiExplanations({});
              localStorage.removeItem('lz_discovery_workshop');
              localStorage.removeItem('lz_discovery_explanations');
              localStorage.removeItem('lz_discovery_ai_answers');
              localStorage.removeItem('lz_discovery_ai_explanations');
              setUnifiedAI(null);
              setUnifiedAIError(null);
              setWorkshopPhase('questionnaire');
              setCurrentCategory(0);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 1.5rem', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600,
              border: '2px solid #6b7280', background: '#fff', color: '#6b7280', cursor: 'pointer',
            }}
          >
            <FiRefreshCw /> Retake Workshop
          </button>
        </div>
      </div>
    );
  }

  return null;
}
